import { createSelector } from 'reselect';
import { RootState } from 'store/types';
import { oc } from 'ts-optchain';
import { getTriageSession } from 'storage/triageSessions/storage';
import { getEndpointRank } from 'lib/triage/utils';

interface TriageRank {
  [key: string]: number;
}

export const triageGyantResultsSelector = (state: RootState) => state.triageGyant.results;

export const triageGyantResultsDataSelector = createSelector(triageGyantResultsSelector, results =>
  oc(results).data({})
);

export const triageGyantCurrentCaseIdSelector = createSelector(
  triageGyantResultsDataSelector,
  results => oc(results).diagnosis.caseId(undefined)
);

export const triageGyantConditionsSortedSelector = createSelector(
  triageGyantResultsDataSelector,
  results => {
    return oc(results)
      .diagnosis.conditions([])
      .slice()
      .sort((a, b) => {
        return b.likelihoodScore - a.likelihoodScore;
      });
  }
);
export const triageGyantEndpointsSortedSelector = createSelector(
  triageGyantResultsDataSelector,
  results => {
    const obj = oc(results).diagnosis.endpoints({});

    const endpointsObj: TriageRank[] = [];
    Object.keys(obj).forEach(key => {
      const rank = getEndpointRank(key);
      if (rank) endpointsObj[key] = rank;
    });

    return Object.keys(endpointsObj).sort((a, b) => {
      return endpointsObj[a] - endpointsObj[b];
    });
  }
);
export const triageGyantResultsIsFetchingSelector = createSelector(
  triageGyantResultsSelector,
  results => results.isFetching
);
export const triageOriginSelector = createSelector(
  triageGyantResultsSelector,
  results => results.origin
);

export const triageGyantConditionDetailSelector = (state: RootState) =>
  state.triageGyant.conditionDetail;
export const triageGyantConditionDetailIsFetchingSelector = createSelector(
  triageGyantConditionDetailSelector,
  conditionDetail => conditionDetail.isFetching
);

export const triageGyantSessionTokenSelector = (state: RootState) =>
  oc(state).triageGyant.sessionToken({});
export const triageGyantSessionTokenIsFetchingSelector = createSelector(
  triageGyantSessionTokenSelector,
  sessionToken => sessionToken.isFetching
);
export const triageGyantResetChatSelector = createSelector(
  triageGyantSessionTokenSelector,
  sessionToken => sessionToken.resetChat
);
export const triageGyantTimeoutSelector = createSelector(
  triageGyantSessionTokenSelector,
  sessionToken => sessionToken.hasTimedOut
);
export const triageGyantSessionTokenDataSelector = createSelector(
  triageGyantSessionTokenSelector,
  sessionToken => oc(sessionToken).data.sessionToken('')
);
export const triageGyantSessionTokenErrorSelector = createSelector(
  triageGyantSessionTokenSelector,
  sessionToken => sessionToken.error
);

export const triageGyantHasFetchedLastSession = createSelector(
  triageGyantSessionTokenSelector,
  sessionToken => !!sessionToken.hasFetchedLastSession
);
export const triageGyantFetchingLastSessionSelector = createSelector(
  triageGyantSessionTokenSelector,
  sessionToken => !!sessionToken.isFetchingLastSession
);
export const triageGyantLastSessionSelector = createSelector(
  triageGyantSessionTokenSelector,
  sessionToken => {
    // Validate that the last token belongs to the current patient. Gyant just gives us a list of tokens in no particular order. This gives the hope that we probably have the last one. If its not in the list, its a different patient.
    if (sessionToken && sessionToken.oldSessions) {
      const lastToken = getTriageSession();
      if (lastToken) {
        return sessionToken.oldSessions.find(
          (oldToken: string) => lastToken.sessionToken === oldToken
        );
      }
    }
    return null;
  }
);

export const triageGyantOldSessionsSelector = createSelector(
  triageGyantSessionTokenSelector,
  sessionToken => sessionToken?.oldSessions || []
);
